/**
 * React-Query hooks for the per-tenant operating-terminology overlay
 * (ADR-126 Decision 3 / ADR-092 / ADR-051).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getTerminology,
  listTerminology,
  getEffectiveTerminology,
  createTerminology,
  updateTerminology,
  deleteTerminology,
  type OperatingTerminologyFilters,
  type OperatingTerminologyWriteInput,
} from '../../services';

export const operatingTerminologyKeys = {
  all: ['operatingTerminology'] as const,
  scope: (filters: OperatingTerminologyFilters) =>
    [...operatingTerminologyKeys.all, 'scope', filters] as const,
  list: (filters: OperatingTerminologyFilters) =>
    [...operatingTerminologyKeys.all, 'list', filters] as const,
  effective: (filters: OperatingTerminologyFilters) =>
    [...operatingTerminologyKeys.all, 'effective', filters] as const,
};

/** The overlay attached directly at this scope (no inheritance). */
export function useTerminology(
  filters: OperatingTerminologyFilters,
  enabled = true,
) {
  return useQuery({
    queryKey: operatingTerminologyKeys.scope(filters),
    queryFn: () => getTerminology(filters),
    enabled: enabled && (filters.district != null || filters.school != null),
  });
}

/** Every overlay the caller can see (across scopes) — for the grouped table. */
export function useTerminologyList(filters: OperatingTerminologyFilters = {}) {
  return useQuery({
    queryKey: operatingTerminologyKeys.list(filters),
    queryFn: () => listTerminology(filters),
  });
}

/** The effective overlay (site override > org default > platform default). */
export function useEffectiveTerminology(
  filters: OperatingTerminologyFilters,
  enabled = true,
) {
  return useQuery({
    queryKey: operatingTerminologyKeys.effective(filters),
    queryFn: () => getEffectiveTerminology(filters),
    enabled: enabled && (filters.district != null || filters.school != null),
  });
}

export function useCreateTerminology() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: OperatingTerminologyWriteInput) =>
      createTerminology(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: operatingTerminologyKeys.all });
    },
  });
}

export function useUpdateTerminology() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: Partial<OperatingTerminologyWriteInput>;
    }) => updateTerminology(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: operatingTerminologyKeys.all });
    },
  });
}

export function useDeleteTerminology() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteTerminology(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: operatingTerminologyKeys.all });
    },
  });
}
